import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AppointmentDetailsDialog from './AppointmentDetailsDialog';
import {
  getCompanyAppointmentUrlSegment,
  isFirestoreAppointmentDocId,
} from '../../shared/booking/appointments/companyAppointmentRouting';
import { normalizeCompanyAppointmentDoc } from '../../calendar_v3/utils/normalizeCompanyAppointment';
import { PageSkeleton } from '../loading/skeletons';
import { getAppointmentOrNull } from '../../api/appointments';
import './styles/AppointmentDetailsPage.css';

const normalizeAppointmentRecord = (record) => {
  if (!record?.id) return record;
  const { id, storageLocation, ...rest } = record;
  return {
    ...normalizeCompanyAppointmentDoc(id, rest),
    storageLocation: storageLocation || 'company',
  };
};

/**
 * Full-page appointment details. Uses appointmentId from URL (may be a short slug when archived/live).
 * Appointment can be passed via location.state.appointment (when navigating from calendar/overview),
 * or fetched from the company appointments API (live or archived when ?archived=1).
 */
const AppointmentDetailsPage = () => {
  const { appointmentId: appointmentParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { companyData } = useAuth();
  const [searchParams] = useSearchParams();
  const fromArchive = Boolean(location.state?.fromArchive || searchParams.get('archived') === '1');

  const [appointment, setAppointment] = useState(
    location.state?.appointment ? normalizeAppointmentRecord(location.state.appointment) : null
  );
  const [loading, setLoading] = useState(!location.state?.appointment);
  const [error, setError] = useState(null);
  const [loadAttempted, setLoadAttempted] = useState(false);
  const isClosingRef = useRef(false);

  useEffect(() => {
    if (location.state?.appointment && !fromArchive) {
      setAppointment(normalizeAppointmentRecord(location.state.appointment));
      setLoading(false);
      setError(null);
      setLoadAttempted(true);
    }
  }, [location.state?.appointment, fromArchive]);

  useEffect(() => {
    if (!companyData?.id) {
      return; // wait for auth; keep loading true to avoid flashing error
    }
    if (!appointmentParam) {
      setLoadAttempted(true);
      setLoading(false);
      setError('Missing appointment id.');
      return;
    }
    isClosingRef.current = false;
    let cancelled = false;
    const seededAppointment = !fromArchive ? location.state?.appointment : null;
    setLoading(!seededAppointment);
    setError(null);
    setLoadAttempted(Boolean(seededAppointment));
    if (seededAppointment) {
      setAppointment(normalizeAppointmentRecord(seededAppointment));
    }

    const loadAppointment = async () => {
      try {
        const record = await getAppointmentOrNull({
          companyId: companyData.id,
          id: appointmentParam,
        });
        if (cancelled) return;
        if (!record) {
          setError(fromArchive ? 'Archived appointment not found.' : 'Appointment not found.');
          setAppointment(null);
          return;
        }
        if (fromArchive && !record.archived) {
          setError('Archived appointment not found.');
          setAppointment(null);
          return;
        }
        if (!fromArchive && record.archived) {
          setError('Appointment not found.');
          setAppointment(null);
          return;
        }
        setAppointment(normalizeAppointmentRecord({ ...record, storageLocation: 'company' }));
        setError(null);
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching appointment:', err);
          setError('Failed to load appointment.');
          setAppointment(null);
        }
      } finally {
        if (!cancelled) {
          setLoadAttempted(true);
          setLoading(false);
        }
      }
    };

    loadAppointment();
    return () => {
      cancelled = true;
    };
  }, [companyData?.id, appointmentParam, location.state?.appointment, fromArchive]);

  useEffect(() => {
    if (loading || !loadAttempted || !appointment || !appointmentParam) return;

    const segment = getCompanyAppointmentUrlSegment(appointment);
    if (!segment || segment === appointmentParam) return;
    if (!isFirestoreAppointmentDocId(appointmentParam) || isFirestoreAppointmentDocId(segment)) return;

    const archivedFlag = searchParams.get('archived') === '1';
    navigate(
      `/app/overview/appointment/${segment}${archivedFlag ? '?archived=1' : ''}`,
      {
        replace: true,
        state: { ...location.state, appointment },
      }
    );
  }, [
    appointment,
    appointmentParam,
    loadAttempted,
    loading,
    location.state,
    navigate,
    searchParams,
  ]);

  const handleClose = () => {
    isClosingRef.current = true;
    navigate(-1);
  };

  const handleUpdate = async () => {
    if (!companyData?.id || !appointment?.id || fromArchive) return;
    try {
      const record = await getAppointmentOrNull({
        companyId: companyData.id,
        id: appointment.id,
      });
      if (record) {
        setAppointment(normalizeAppointmentRecord({ ...record, storageLocation: 'company' }));
      }
    } catch (err) {
      console.error('Error refetching appointment:', err);
    }
  };

  const handleBack = () => {
    isClosingRef.current = true;
    navigate(-1);
  };

  if (loading || !loadAttempted) {
    return (
      <div className="appointment-details-page">
        <PageSkeleton padded variant="form" label="Loading appointment" />
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="appointment-details-page">
        <div className="appointment-details-page__main">
          <button type="button" className="appointment-details-page__back-link" onClick={handleBack}>← Back</button>
          <div className="appointment-details-page__error">
            <p>{error || 'Appointment not found.'}</p>
            <button type="button" className="appointment-details-page__error-dismiss" onClick={handleBack}>Go back</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="appointment-details-page">
      <div className="appointment-details-page__main">
        <button type="button" className="appointment-details-page__back-link" onClick={handleBack}>
          ← Back
        </button>
        <AppointmentDetailsDialog
          appointment={appointment}
          open={true}
          onClose={handleClose}
          onUpdate={handleUpdate}
          onLiveAppointmentWillBeDeleted={() => {
            isClosingRef.current = true;
          }}
          variant="page"
          isArchived={fromArchive}
        />
      </div>
    </div>
  );
};

export default AppointmentDetailsPage;
